import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Tab, Tabs } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { getProfile } from '../redux/slices/authSlice';
import { usersAPI } from '../services/api';

const UserSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Profile form
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    job_title: '',
    department: ''
  });

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        phone: currentUser.phone || '',
        job_title: currentUser.job_title || '',
        department: currentUser.department || ''
      });
    }
  }, [currentUser]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (currentUser) {
        await usersAPI.update(String(currentUser.id), profileData);
        await dispatch(getProfile()).unwrap();
        setSuccessMessage('Profile updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    // Validate password match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      if (currentUser) {
        await usersAPI.update(String(currentUser.id), {
          password: passwordData.new_password
        });
        setSuccessMessage('Password updated successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{
            color: '#00579a',
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Account Settings
          </h1>
          <p style={{ color: '#595959', marginTop: '8px' }}>
            Manage your personal information and security settings
          </p>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '1.5rem'
            }}
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMessage('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #b7eb8f',
              backgroundColor: '#f6ffed',
              marginBottom: '1.5rem'
            }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Account Info Card */}
        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '1.5rem'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <h5 style={{ color: '#00579a', fontWeight: 600, marginBottom: '20px' }}>
              Account Information
            </h5>
            <Row>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Username:</strong>
                  <div style={{ fontSize: '16px', color: '#262626', marginTop: '4px' }}>
                    {currentUser?.username}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Email:</strong>
                  <div style={{ fontSize: '16px', color: '#262626', marginTop: '4px' }}>
                    {currentUser?.email}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Role:</strong>
                  <div style={{ fontSize: '16px', color: '#262626', marginTop: '4px', textTransform: 'capitalize' }}>
                    {currentUser?.role}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Organization:</strong>
                  <div style={{ fontSize: '16px', color: '#262626', marginTop: '4px' }}>
                    {currentUser?.organization_id}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Settings Tabs */}
        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)'
        }}>
          <Card.Body style={{ padding: 0 }}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || 'profile')}
              className="mb-3"
              style={{ borderBottom: '1px solid #f0f0f0', padding: '0 24px' }}
            >
              <Tab
                eventKey="profile"
                title="Profile"
                style={{ padding: '24px' }}
              >
                <Form onSubmit={handleProfileSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profileData.first_name}
                          onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                          style={inputStyle}
                          placeholder="John"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={profileData.last_name}
                          onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                          style={inputStyle}
                          placeholder="Doe"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          style={inputStyle}
                          placeholder="+264 81 234 5678"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Job Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={profileData.job_title}
                          onChange={(e) => setProfileData({ ...profileData, job_title: e.target.value })}
                          style={inputStyle}
                          placeholder="Software Engineer"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Department</Form.Label>
                    <Form.Control
                      type="text"
                      value={profileData.department}
                      onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                      style={inputStyle}
                      placeholder="IT Department"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: '#00579a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 24px',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </Form>
              </Tab>

              <Tab
                eventKey="security"
                title="Security"
                style={{ padding: '24px' }}
              >
                <Form onSubmit={handlePasswordSubmit}>
                  <Alert variant="info" style={{ marginBottom: '20px', fontSize: '14px' }}>
                    <strong>Password Requirements:</strong>
                    <ul style={{ marginBottom: 0, marginTop: '8px', paddingLeft: '20px' }}>
                      <li>At least 8 characters long</li>
                      <li>Passwords must match</li>
                    </ul>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>New Password *</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      required
                      style={inputStyle}
                      placeholder="Enter new password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Confirm New Password *</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      required
                      style={inputStyle}
                      placeholder="Confirm new password"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: '#00579a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 24px',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
                      }}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </div>
                </Form>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px'
};

export default UserSettings;